import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../../data";

const Logo = () => (
  <a
    href="#top"
    data-testid="header-logo"
    className="group flex items-center gap-2.5 select-none"
  >
    <span className="relative flex h-8 w-8 items-center justify-center border border-white/20 bg-[#00F0FF]">
      <span className="font-heading text-xl font-black leading-none text-black">F</span>
    </span>
    <span className="font-heading text-xl font-extrabold uppercase tracking-tight text-white leading-none">
      Fuel<span className="text-[#00F0FF]">Factory</span>
    </span>
  </a>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "border-white/10 bg-black/70 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <Logo />

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className="font-mono text-xs uppercase tracking-[0.15em] text-white/60 transition-colors duration-200 hover:text-[#00F0FF]"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid="header-cta-button"
            className="hidden bg-[#00F0FF] px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.12em] text-black transition-transform duration-200 hover:scale-105 sm:inline-block"
          >
            Book Assessment
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="flex h-10 w-10 items-center justify-center border border-white/15 text-white lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-4">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="border-b border-white/5 py-3 font-heading text-2xl uppercase tracking-tight text-white/80 transition-colors hover:text-[#00F0FF]"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="mt-4 bg-[#00F0FF] px-5 py-3 text-center font-mono text-xs font-bold uppercase tracking-[0.12em] text-black"
              >
                Book Performance Assessment
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
